/**
 * Die Kamera in der Hand des Betrachters.
 *
 * OHNE `three`, und das ist keine Stilfrage. Der Zustand liegt im
 * SimulationProvider, damit ein Knopf „Zentrieren" ihn anfassen kann — und der
 * Provider liegt im Hauptbündel. Ein `three`-Import hier zog die ganze
 * Bibliothek dorthin: die Anmeldeseite lud plötzlich ein Megabyte 3D mit, und
 * der eigene Chunk der Bühne schrumpfte von 246 kB auf 8. Sichtbar wurde das
 * erst am Build.
 *
 * WARUM ES DAS BRAUCHT. Die Bühne fährt ihre Kamera bisher selbst: jede Seite
 * hat eine Pose, jede Ansicht einen festen Winkel, und dazwischen wird
 * geglitten. Das ist richtig für eine Bühne, auf der etwas AUSGESTELLT wird —
 * und falsch, sobald jemand etwas ANSEHEN will. Wer prüfen möchte, ob die
 * Schulter beim Armschwung an den Brustkorb stösst, muss von schräg hinten
 * heranfahren können. Eine Ansicht, die er nicht selbst wählen kann, beantwortet
 * die Frage nicht.
 *
 * DIE ÜBERNAHME IST EIN ZUSTAND UND KEIN MODUS. Solange niemand zieht, fährt die
 * Bühne wie bisher. Beim ersten Ziehen wird der aktuelle Stand ÜBERNOMMEN — die
 * Kamera bleibt, wo sie ist, und gehorcht ab da der Maus. Das ist der
 * Unterschied zwischen einem Bedienelement und einem Sprung: ohne Übernahme
 * ruckte die Kamera beim ersten Anfassen in eine Ausgangslage, die niemand
 * angefordert hat. `release()` gibt sie zurück, und sie gleitet in die Pose.
 *
 * REINE RECHNUNG. Kein DOM, keine Ereignisse, keine Zeit — die kommen aus
 * `useStageOrbit`. So lässt sich prüfen, was sonst nur zu erfühlen wäre: dass
 * man nicht unter den Boden kippt, dass der Zoom nicht durch die Figur
 * hindurchfährt, dass zwei entgegengesetzte Drehungen wieder am Anfang landen.
 */

/** Ein Punkt. Absichtlich kein `THREE.Vector3` — siehe oben. */
export interface Vec3 {
  x: number
  y: number
  z: number
}

export interface OrbitState {
  /** Hat der Betrachter die Kamera übernommen? */
  taken: boolean
  /** Azimut um die Hochachse, rad. */
  theta: number
  /** Polarwinkel von oben, rad. Klein = von oben, gross = von unten. */
  phi: number
  /** Abstand zum Blickpunkt, m. */
  dist: number
  /** Der Blickpunkt selbst. */
  target: Vec3
}

/**
 * Wie weit man kippen darf.
 *
 * Nicht bis zu den Polen: genau senkrecht von oben verliert die Drehung ihren
 * Bezug — der Azimut dreht dann das Bild um sich selbst, und man weiss nicht
 * mehr, wo vorn war. Unten ist bei knapp über der Waagerechten Schluss: ein
 * Blick von unter dem Boden zeigt die Rückseite der Bodenebene und sonst
 * nichts.
 */
export const PHI_RANGE = { min: 0.12, max: Math.PI / 2 + 0.16 } as const

/**
 * Wie nah und wie weit.
 *
 * Die untere Grenze ist keine Willkür: die Figur ist rund 1,76 m hoch, und
 * unter etwa einem Meter Abstand steckt die Kamera in ihr. Die obere Grenze
 * hält sie davor, in der Landschaft zu verschwinden.
 */
export const DIST_RANGE = { min: 1.1, max: 26 } as const

/** Wie weit der Blickpunkt vom Ursprung wegwandern darf, m. */
const PAN_LIMIT = 12

/** Bogenmass je Pixel beim Ziehen. */
const TURN_PER_PIXEL = 0.0052

/** Ein Rad-Rasterschritt ändert den Abstand um diesen Faktor. */
const ZOOM_PER_UNIT = 0.0012

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v)

export const orbitStart = (): OrbitState => ({
  taken: false,
  theta: 0,
  phi: Math.PI / 2,
  dist: 4,
  target: { x: 0, y: 0, z: 0 },
})

/**
 * Übernimmt den aktuellen Stand der Bühne.
 *
 * DER ENTSCHEIDENDE SCHRITT und der einzige, der die Kamera lesen muss statt sie
 * zu schreiben. Ohne ihn springt das Bild beim ersten Anfassen dorthin, wo die
 * Ausgangswerte zufällig stehen.
 */
export function orbitTake(state: OrbitState, cameraAt: Vec3, lookAt: Vec3): OrbitState {
  if (state.taken) return state
  const dx = cameraAt.x - lookAt.x
  const dy = cameraAt.y - lookAt.y
  const dz = cameraAt.z - lookAt.z
  const radius = Math.hypot(dx, dy, dz)
  return {
    taken: true,
    /* atan2(x, z) und nicht (z, x): der Azimut wird von der +z-Achse gezählt,
       wie `orbitPosition` ihn wieder auflöst. Vertauscht stimmt der Winkel
       zwar, aber die Rückrechnung landet um 90° daneben. */
    theta: Math.atan2(dx, dz),
    phi: clamp(radius > 0 ? Math.acos(dy / radius) : Math.PI / 2, PHI_RANGE.min, PHI_RANGE.max),
    dist: clamp(radius, DIST_RANGE.min, DIST_RANGE.max),
    target: { ...lookAt },
  }
}

/** Drehen. Waagerechter Zug dreht um die Hochachse, senkrechter kippt. */
export function orbitTurn(state: OrbitState, dx: number, dy: number): OrbitState {
  return {
    ...state,
    theta: state.theta - dx * TURN_PER_PIXEL,
    phi: clamp(state.phi - dy * TURN_PER_PIXEL, PHI_RANGE.min, PHI_RANGE.max),
  }
}

/**
 * Schwenken — der Blickpunkt wandert quer zur Blickrichtung.
 *
 * Der Versatz wird mit dem ABSTAND skaliert. Ein Schwenk von hundert Pixeln
 * muss aus zwei Metern eine kleinere Strecke sein als aus zwanzig; ohne diese
 * Kopplung schiebt man aus der Nähe zentimeterweise und aus der Ferne über die
 * halbe Landschaft.
 */
export function orbitPan(
  state: OrbitState,
  dx: number,
  dy: number,
  /** Die Rechts- und Hochachse der Kamera. Der Aufrufer liest sie aus ihrer Matrix. */
  right: Vec3,
  up: Vec3,
  viewportHeight: number,
  fovDegrees: number,
): OrbitState {
  /* Wie viele Weltmeter ein Bildschirmpixel auf Höhe des Blickpunkts bedeutet. */
  const worldPerPixel =
    (2 * state.dist * Math.tan((fovDegrees * Math.PI) / 360)) / Math.max(1, viewportHeight)

  const move = (a: number, r: number, u: number) =>
    clamp(a - dx * worldPerPixel * r + dy * worldPerPixel * u, -PAN_LIMIT, PAN_LIMIT)

  return {
    ...state,
    target: {
      x: move(state.target.x, right.x, up.x),
      y: move(state.target.y, right.y, up.y),
      z: move(state.target.z, right.z, up.z),
    },
  }
}

/**
 * Zoomen.
 *
 * Multiplikativ und nicht additiv: aus zwanzig Metern soll ein Rasterschritt
 * spürbar heranholen, aus anderthalb Metern nicht durch die Figur hindurch. Ein
 * fester Betrag je Schritt kann nur eines von beidem.
 */
export function orbitZoom(state: OrbitState, deltaY: number): OrbitState {
  return {
    ...state,
    dist: clamp(state.dist * Math.exp(deltaY * ZOOM_PER_UNIT), DIST_RANGE.min, DIST_RANGE.max),
  }
}

/** Zurück an die Bühne. Sie gleitet von hier in ihre Pose. */
export const orbitRelease = (state: OrbitState): OrbitState => ({ ...state, taken: false })

/** Nur den Blickpunkt zurücksetzen — Drehung und Abstand bleiben. */
export const orbitRecentre = (state: OrbitState, y: number): OrbitState => ({
  ...state,
  target: { x: 0, y, z: 0 },
})

/** Wo die Kamera dann steht. */
export function orbitPosition(state: OrbitState): Vec3 {
  const sinPhi = Math.sin(state.phi)
  return {
    x: state.target.x + state.dist * sinPhi * Math.sin(state.theta),
    y: state.target.y + state.dist * Math.cos(state.phi),
    z: state.target.z + state.dist * sinPhi * Math.cos(state.theta),
  }
}
